'use client'

import {captureException} from '@sentry/browser'
import {logger} from 'common'
import {redirect} from 'next/navigation'

import {useLanguage} from '@/utils/i18n/language'

/*
 * Sits at the `[locale]` level so it covers both route groups — a statically rendered page gets
 * the same /500 as a dynamic one.
 *
 * Next requires `error.tsx` to be a client component, so it is never evaluated during static
 * generation and cannot make a route unprerenderable - the same reason `not-found.tsx` is one.
 * `useLanguage()` reads from `NextIntlClientProvider`, which `AppLayout` renders in this segment's
 * layout, above this boundary.
 *
 * It does not catch errors thrown by that layout itself — an error in `AppLayout` or
 * `SharedProviders` bubbles past this to `app/global-error.tsx`, which supplies its own document
 * precisely because the layout that normally would has just failed.
 */
interface ErrorProps {
  error: Error & {digest?: string}
  reset: () => void
}

export default function Error({error}: ErrorProps) {
  logger.error(error, error.message)
  // Not in an effect: `redirect()` throws during render, so effects on this boundary never run.
  captureException(error)
  const language = useLanguage()
  redirect(`/${language}/500`)
}
